/**
 * Angle Role - Convert Pillar
 *
 * Conversão entre unidades de ângulo usando hub-and-spoke (via degree).
 *
 * @example
 * import { convertAngle, toBaseAngle, fromBaseAngle } from '@attrx/role-morphic/angle/convert';
 *
 * convertAngle('degree', 'radian', 180);  // π ≈ 3.14159
 * toBaseAngle('turn', 1);                 // 360 (degrees)
 * fromBaseAngle('radian', 180);           // π ≈ 3.14159
 */

import { ANGLE_UNITS, type AngleUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

// =============================================================================
// CONSTANTS
// =============================================================================

/** Unidade base para conversões (hub) */
export const ANGLE_BASE: AngleUnit = "degree";

// =============================================================================
// CORE CONVERSION
// =============================================================================

/**
 * Converte valor para a unidade base (degree)
 *
 * @param variant - Unidade de origem
 * @param value - Valor a converter
 * @returns Valor em degrees
 *
 * @example
 * toBaseAngle('turn', 1);      // 360
 * toBaseAngle('radian', Math.PI); // 180
 */
export function toBaseAngle(variant: AngleUnit, value: number): number {
  const config = ANGLE_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown angle unit: ${variant}`);
  }
  return value * (config.factor ?? 1);
}

/**
 * Converte valor da unidade base (degree) para outra unidade
 *
 * @param variant - Unidade de destino
 * @param baseValue - Valor em degrees
 * @returns Valor na unidade de destino
 *
 * @example
 * fromBaseAngle('radian', 180);  // π ≈ 3.14159
 * fromBaseAngle('turn', 360);    // 1
 */
export function fromBaseAngle(variant: AngleUnit, baseValue: number): number {
  const config = ANGLE_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown angle unit: ${variant}`);
  }
  return baseValue / (config.factor ?? 1);
}

/**
 * Converte entre duas unidades de ângulo
 *
 * @param from - Unidade de origem
 * @param to - Unidade de destino
 * @param value - Valor a converter
 * @returns Valor convertido
 *
 * @example
 * convertAngle('degree', 'radian', 180);  // π ≈ 3.14159
 * convertAngle('turn', 'degree', 1);      // 360
 */
export function convertAngle(
  from: AngleUnit,
  to: AngleUnit,
  value: number,
): number {
  if (from === to) {
    return value;
  }
  const baseValue = toBaseAngle(from, value);
  return fromBaseAngle(to, baseValue);
}

/**
 * Versão safe de convertAngle que retorna Result
 *
 * @example
 * tryConvertAngle('degree', 'radian', 180);  // { ok: true, value: π }
 * tryConvertAngle('invalid', 'radian', 180); // { ok: false, error: '...' }
 */
export function tryConvertAngle(
  from: AngleUnit,
  to: AngleUnit,
  value: number,
): Result<number> {
  try {
    return { ok: true, value: convertAngle(from, to, value) };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Verifica se uma variante existe
 *
 * @example
 * hasAngleVariant('degree');   // true
 * hasAngleVariant('invalid');  // false
 */
export function hasAngleVariant(variant: string): variant is AngleUnit {
  return variant in ANGLE_UNITS;
}

/**
 * Retorna lista de todas as variantes disponíveis
 *
 * @example
 * getAngleVariants();  // ['turn', 'degree', 'arcminute', ...]
 */
export function getAngleVariants(): AngleUnit[] {
  return Object.keys(ANGLE_UNITS) as AngleUnit[];
}

// =============================================================================
// CONVENIENCE FUNCTIONS
// =============================================================================

/**
 * Converte graus para radianos
 *
 * @example
 * degreesToRadians(180) // π ≈ 3.14159
 * degreesToRadians(90)  // π/2 ≈ 1.5708
 */
export function degreesToRadians(degrees: number): number {
  return convertAngle("degree", "radian", degrees);
}

/**
 * Converte radianos para graus
 *
 * @example
 * radiansToDegrees(Math.PI)     // 180
 * radiansToDegrees(Math.PI / 2) // 90
 */
export function radiansToDegrees(radians: number): number {
  return convertAngle("radian", "degree", radians);
}

/**
 * Normaliza ângulo para o intervalo [0, 360)
 *
 * @example
 * normalizeAngle(450)  // 90
 * normalizeAngle(-90)  // 270
 * normalizeAngle(360)  // 0
 */
export function normalizeAngle(degrees: number): number {
  const normalized = degrees % 360;
  // Evita -0 (JavaScript tem +0 e -0)
  if (normalized === 0) return 0;
  return normalized < 0 ? normalized + 360 : normalized;
}
